import { useState } from "react";
import { useTranslations } from "use-intl";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { UserPlus, Shield, Users } from "lucide-react";
import { toast } from "sonner";
import { useFormatters } from "@financialharmony/ui";

type Role = "administrator" | "member" | "viewer";

interface PermissionUser {
  id: string;
  email: string;
  role: Role;
  status: string;
  joinedAt: string;
}

type PermissionError = "onlyAdmins" | "lastAdmin";

const ROLES: Role[] = ["administrator", "member", "viewer"];

const UserPermissions = () => {
  const t = useTranslations("consultantOnboarding.admin.permissions");
  const tc = useTranslations("common.actions");
  const { formatDate } = useFormatters();

  // Mock data for now - will be replaced with actual API calls
  const [users, setUsers] = useState<PermissionUser[]>([{
    id: "1",
    email: "bastiaanweide@gmail.com",
    role: "administrator",
    status: "active",
    joinedAt: "2024-01-15"
  }, {
    id: "2",
    email: "john.doe@example.com",
    role: "member",
    status: "active",
    joinedAt: "2024-02-20"
  }]);

  // Current user (mock - in real app this would come from auth context)
  const currentUser = users[0]; // Assuming first user is current user for demo

  // Dialog states
  const [roleChangeDialog, setRoleChangeDialog] = useState<{open: boolean, user: PermissionUser | null}>({open: false, user: null});
  const [errorDialog, setErrorDialog] = useState<{open: boolean, error: PermissionError | null}>({open: false, error: null});
  const [selectedRole, setSelectedRole] = useState<string>("");

  // Check if current user is administrator
  const isCurrentUserAdmin = currentUser?.role === "administrator";

  // Count administrators
  const adminCount = users.filter(user => user.role === "administrator").length;

  // Check if user is the last admin
  const isLastAdmin = (user: PermissionUser) => user.role === "administrator" && adminCount === 1;

  const handleChangeRoleClick = (user: PermissionUser) => {
    if (!isCurrentUserAdmin) {
      setErrorDialog({ open: true, error: "onlyAdmins" });
      return;
    }

    if (isLastAdmin(user)) {
      setErrorDialog({ open: true, error: "lastAdmin" });
      return;
    }

    setSelectedRole(user.role);
    setRoleChangeDialog({open: true, user});
  };

  const handleRoleChange = () => {
    const target = roleChangeDialog.user;
    if (!target || !selectedRole) return;
    const newRole = ROLES.find((role) => role === selectedRole);
    if (!newRole) return;

    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === target.id
          ? {...user, role: newRole}
          : user
      )
    );

    toast.success(t("toastTitle"), {
      description: t("toastDescription", { email: target.email, role: t(`roleNames.${newRole}`) }),
    });

    setRoleChangeDialog({open: false, user: null});
    setSelectedRole("");
  };
  const getRoleBadge = (role: Role) => {
    const variants = {
      administrator: "default",
      member: "secondary",
      viewer: "outline"
    } as const;
    const colors = {
      administrator: "bg-red-100 text-red-800",
      member: "bg-blue-100 text-blue-800",
      viewer: "bg-gray-100 text-gray-800"
    } as const;
    return <Badge variant={variants[role]} className={colors[role]}>
        {t(`roles.${role}`)}
      </Badge>;
  };
  return <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-muted-foreground">
            {t("description")}
          </p>
        </div>
        <Button className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          {t("invite")}
        </Button>
      </div>

      {/* Permission Levels Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t("levelsTitle")}
          </CardTitle>
          <CardDescription>
            {t("levelsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="bg-red-100 text-red-800 min-w-[100px] justify-center hover:bg-red-100 hover:text-red-800">{t("roles.administrator")}</Badge>
              <div className="flex-1">
                <p className="font-medium">{t("levels.fullTitle")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("levels.fullBody")}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 min-w-[100px] justify-center hover:bg-blue-100 hover:text-blue-800">{t("roles.member")}</Badge>
              <div className="flex-1">
                <p className="font-medium">{t("levels.standardTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("levels.standardBody")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="bg-gray-100 text-gray-800 min-w-[100px] justify-center">{t("roles.viewer")}</Badge>
              <div className="flex-1">
                <p className="font-medium">{t("levels.readOnlyTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("levels.readOnlyBody")}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("usersTitle", { count: String(users.length) })}
          </CardTitle>
          <CardDescription>
            {t("usersDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users.map(user => <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium">{user.email}</p>
                      <p className="text-sm text-muted-foreground">
                        {t("joined", { date: formatDate(user.joinedAt) })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getRoleBadge(user.role)}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChangeRoleClick(user)}
                  >
                    {t("changeRole")}
                  </Button>
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>

      {/* Role Change Dialog */}
      <Dialog open={roleChangeDialog.open} onOpenChange={(open) => setRoleChangeDialog({open, user: null})}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("dialogDescription", { email: roleChangeDialog.user?.email ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder={t("selectPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((role) => (
                  <SelectItem key={role} value={role}>{t(`roles.${role}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRoleChangeDialog({open: false, user: null})}>
              {tc("cancel")}
            </Button>
            <Button onClick={handleRoleChange} disabled={!selectedRole || selectedRole === roleChangeDialog.user?.role}>
              {t("updateRole")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <AlertDialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog({open, error: null})}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deniedTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {errorDialog.error && t(`errors.${errorDialog.error}`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorDialog({open: false, error: null})}>
              {t("ok")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default UserPermissions;
